import { useEffect, useState } from "react";
import api, { fileUrl, errMsg } from "../lib/api";
import { LOGO_URL } from "../lib/constants";
import { formatMoney as currency } from "../lib/money";
import jsPDF from "jspdf";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const COMPANY = { name: "Grian Solar Power", abn: "91 635 362 560", address: "18 Capital Avenue, Glen Waverley VIC 3150", phone: "0485 976 518", email: "info@griansolarpower.com.au", website: "griansolarpower.com.au" };
const METHODS = [
  { v: "bank_transfer", l: "Bank Transfer" }, { v: "card", l: "Card" },
  { v: "cash", l: "Cash" }, { v: "bpay", l: "BPAY" },
];
const FINANCE_LABELS = { plenti: "Plenti Finance", brighte: "Brighte Finance" };
const emptyPay = () => ({ date: "", amount: "", method: "bank_transfer", receipt_id: null, verified: false });

const loadImg = (src) => new Promise((res) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
const fmtAUDate = (d) => { if (!d) return ""; try { const [y, m, dd] = d.slice(0, 10).split("-"); return `${dd}/${m}/${y}`; } catch { return d; } };
const itemDesc = (it) => it.description || `${it.qty || 1} X ${it.brand || ""} ${it.model_no || ""}`.trim();

async function buildInvoicePdf(inv) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, H = 297, mL = 12, mR = 12, rx = W - mR, cw = W - mL - mR;
  const GREEN = [22, 101, 52], LGREEN = [240, 250, 244], BORDER = [203, 213, 225], DARK = [15, 23, 42], MUTE = [100, 116, 139];
  const co = { ...COMPANY, ...(inv.company || {}) };
  let y = 0;

  // ---------- helpers ----------
  const setF = (size, style = "normal", color = DARK) => { doc.setFontSize(size); doc.setFont(undefined, style); doc.setTextColor(...color); };
  const pageFrame = () => { doc.setDrawColor(...BORDER); doc.setLineWidth(0.35); doc.rect(6, 6, W - 12, H - 12); };
  const ensure = (need) => { if (y + need > H - 12) { doc.addPage(); pageFrame(); y = 14; } };
  const sectionBar = (title, x, w, h = 6.2) => {
    doc.setFillColor(...GREEN); doc.rect(x, y, w, h, "F");
    setF(8.6, "bold", [255, 255, 255]); doc.text(title.toUpperCase(), x + 3, y + h - 1.9);
  };

  pageFrame();

  // ---------- header: logo (original proportions) left · TAX INVOICE + company column right ----------
  y = 13;
  const logo = await loadImg(LOGO_URL);
  if (logo && logo.width && logo.height) {
    const maxW = 52, maxH = 21;
    const sc = Math.min(maxW / logo.width, maxH / logo.height);
    doc.addImage(logo, "PNG", mL, y + 1, logo.width * sc, logo.height * sc);
  }
  setF(22, "bold", GREEN); doc.text("TAX INVOICE", rx, y + 7, { align: "right" });
  setF(8.6, "normal", MUTE);
  doc.text(`Invoice No: ${inv.invoice_no || ""}    ·    Date: ${new Date().toLocaleDateString("en-AU")}`, rx, y + 12.2, { align: "right" });
  setF(8.4, "bold", DARK);
  doc.text(`${co.name}  ·  ABN ${co.abn}`, rx, y + 17.4, { align: "right" });
  setF(7.8, "normal", MUTE);
  doc.text(String(co.address || ""), rx, y + 21.4, { align: "right" });
  doc.text([co.phone && `Ph: ${co.phone}`, co.email, co.website && String(co.website).replace(/^https?:\/\//, "").replace(/\/$/, "")].filter(Boolean).join("  ·  "), rx, y + 25.2, { align: "right" });
  y += 28.5;
  doc.setDrawColor(...GREEN); doc.setLineWidth(0.8); doc.line(mL, y, rx, y);
  doc.setLineWidth(0.2); y += 4.5;

  // ---------- customer + installation boxes ----------
  const c = inv.customer || {}, p = inv.property || {};
  const colW = (cw - 6) / 2, x2 = mL + colW + 6;
  const custPairs = [["Name", c.name], ["Address", c.address], ["Phone", c.phone], ["Email", c.email]].filter(([, v]) => v);
  const instPairs = [["Roof Type", p.roof_type], ["No. of Storey", p.no_of_stories], ["Roof Pitch", p.roof_pitch], ["No. of Phases", p.phase],
    ["Switch Board", p.switchboard || "N/A"], ["Property NMI", p.nmi_no], ["Retailer", p.retailer], ["Distributor", p.distributor]].filter(([, v]) => v);
  const pairLines = (pairs, w) => pairs.reduce((n, [, v]) => n + doc.splitTextToSize(String(v), w - 32).length, 0);
  setF(8.2);
  const boxRows = Math.max(pairLines(custPairs, colW), pairLines(instPairs, colW));
  const boxH = 6.2 + boxRows * 4.2 + 3;
  ensure(boxH + 4);
  const drawPairsBox = (title, pairs, x) => {
    doc.setDrawColor(...BORDER); doc.rect(x, y, colW, boxH);
    sectionBar(title, x, colW);
    let yy = y + 10.4;
    pairs.forEach(([k, v]) => {
      setF(7.9, "bold", MUTE); doc.text(`${k}`, x + 3, yy);
      setF(8.1, "normal", DARK);
      const lines = doc.splitTextToSize(String(v), colW - 32);
      lines.forEach((ln, i) => { doc.text(ln, x + 30, yy + i * 4.2); });
      yy += lines.length * 4.2;
    });
  };
  drawPairsBox("Bill To", custPairs, mL);
  drawPairsBox("Installation Details", instPairs, x2);
  y += boxH + 4.5;

  // ---------- product description table ----------
  const items = (inv.line_items || []).map((it) => [it.category, itemDesc(it), String(it.qty || 1)]);
  if (inv.show_racking !== false) items.push(["Racking System", "Rails & Rackings as per Australian Standard", "1"]);
  const descH = inv.system_description ? 5.5 : 0;
  ensure(6.2 + descH + items.length * 6 + 8);
  sectionBar("Product Description", mL, cw); y += 6.2;
  if (inv.system_description) {
    doc.setFillColor(...LGREEN); doc.rect(mL, y, cw, 5.5, "F"); doc.setDrawColor(...BORDER); doc.rect(mL, y, cw, 5.5);
    setF(8.3, "bold", GREEN); doc.text(inv.system_description, mL + 3, y + 3.8); y += 5.5;
  }
  const qtyW = 14, compW = 38, descW = cw - compW - qtyW;
  doc.setFillColor(248, 250, 249); doc.setDrawColor(...BORDER);
  doc.rect(mL, y, compW, 5.2, "FD"); doc.rect(mL + compW, y, descW, 5.2, "FD"); doc.rect(mL + compW + descW, y, qtyW, 5.2, "FD");
  setF(7.4, "bold", MUTE);
  doc.text("COMPONENT", mL + 3, y + 3.6); doc.text("DESCRIPTION", mL + compW + 3, y + 3.6); doc.text("QTY", mL + compW + descW + qtyW / 2, y + 3.6, { align: "center" });
  y += 5.2;
  items.forEach(([comp, desc, qty], i) => {
    const lines = doc.splitTextToSize(desc, descW - 6);
    const rh = Math.max(5.8, lines.length * 4 + 2.2);
    ensure(rh);
    if (i % 2 === 1) { doc.setFillColor(250, 251, 250); doc.rect(mL, y, cw, rh, "F"); }
    doc.setDrawColor(...BORDER);
    doc.rect(mL, y, compW, rh); doc.rect(mL + compW, y, descW, rh); doc.rect(mL + compW + descW, y, qtyW, rh);
    setF(8, "bold", DARK); doc.text(comp, mL + 3, y + 4);
    setF(8, "normal", DARK); lines.forEach((ln, li) => doc.text(ln, mL + compW + 3, y + 4 + li * 4));
    setF(8, "bold", DARK); doc.text(qty, mL + compW + descW + qtyW / 2, y + 4, { align: "center" });
    y += rh;
  });

  // additional notes below product description
  if (inv.product_notes) {
    setF(7.8, "italic", MUTE);
    const nl = doc.splitTextToSize(inv.product_notes, cw - 6);
    const nh = nl.length * 3.9 + 3.4;
    ensure(nh);
    doc.setFillColor(252, 253, 252); doc.setDrawColor(...BORDER); doc.rect(mL, y, cw, nh, "FD");
    nl.forEach((ln, i) => doc.text(ln, mL + 3, y + 3.4 + i * 3.9));
    y += nh;
  }
  y += 3;

  // ---------- installation date ----------
  ensure(6.5 + 4);
  const lblW = 52;
  doc.setFillColor(...GREEN); doc.rect(mL, y, lblW, 6.2, "F");
  setF(8.6, "bold", [255, 255, 255]); doc.text("INSTALLATION DATE", mL + 3, y + 4.3);
  doc.setDrawColor(...BORDER); doc.rect(mL + lblW, y, cw - lblW, 6.2);
  setF(8.6, "bold", inv.installation_date ? DARK : MUTE);
  doc.text(inv.installation_date ? fmtAUDate(inv.installation_date) : "To be confirmed", mL + lblW + 3, y + 4.3);
  y += 6.2 + 4;

  // ---------- pricing table ----------
  const financeLbl = FINANCE_LABELS[inv.finance_provider];
  const priceRows = [];
  const pr = (l, v, o = {}) => priceRows.push({ l, v, ...o });
  pr("Total Cost (Incl GST)", inv.total_cost_incl_gst, { bold: true });
  if (inv.stc_amount > 0) pr(`Less ${inv.stc_count} STC @ ${currency(inv.stc_price)}`, -inv.stc_amount, { muted: true });
  if (inv.battery_stc_amount > 0) pr(`Less ${inv.battery_stc_count} Battery STC @ ${currency(inv.stc_price)}`, -inv.battery_stc_amount, { muted: true });
  pr("Final Quote Price (Incl. GST)", inv.final_quote_price, { bold: true, fill: true });
  if (inv.vic_rebate > 0) pr("Less Solar Victoria Rebate", -inv.vic_rebate, { muted: true });
  if (inv.vic_loan > 0) pr("Less Solar Victoria Loan", -inv.vic_loan, { muted: true });
  pr("Balance Payable", inv.balance_payable, { bold: true, fill: true });
  pr("Deposit", inv.deposit || 0, { muted: true });
  pr("Balance Payment Received", inv.amount_received || 0, { muted: true });
  if (financeLbl) {
    if (inv.finance_amount > 0) pr(`Payment via ${financeLbl}`, -inv.finance_amount, { muted: true });
    else pr(`Payment to be arranged by ${financeLbl}`, null, { finance: true });
  }
  ensure(6.2 + priceRows.length * 6.2 + 9 + 6);
  sectionBar("Pricing Summary", mL, cw); y += 6.2;
  const amtW = 42;
  priceRows.forEach((r) => {
    const rh = 6.2;
    ensure(rh);
    if (r.fill) { doc.setFillColor(...LGREEN); doc.rect(mL, y, cw, rh, "F"); }
    doc.setDrawColor(...BORDER); doc.rect(mL, y, cw - amtW, rh); doc.rect(rx - amtW, y, amtW, rh);
    if (r.finance) {
      setF(8.2, "bolditalic", GREEN);
      doc.text(r.l, mL + 3, y + 4.3);
    } else {
      setF(r.bold ? 8.7 : 8.2, r.bold ? "bold" : "normal", r.fill ? GREEN : r.muted ? MUTE : DARK);
      doc.text(r.l, mL + 3, y + 4.3);
      doc.text(currency(r.v), rx - 3, y + 4.3, { align: "right" });
    }
    y += rh;
  });
  // final due band
  ensure(9 + 6);
  doc.setFillColor(...GREEN); doc.rect(mL, y, cw, 8.5, "F");
  setF(10, "bold", [255, 255, 255]);
  doc.text("BALANCE PAYMENT DUE", mL + 3, y + 5.7);
  doc.text(currency(inv.balance_due), rx - 3, y + 5.7, { align: "right" });
  y += 8.5 + 5;

  // ---------- warranties + payment boxes ----------
  const w = inv.warranties || {};
  const wr = [["Workmanship Warranty", w.workmanship], ["Inverter Warranty", w.inverter], ["Panel Manufacturing Warranty", w.panel_product], ["Panel Performance Warranty", w.panel_performance], ["Battery Warranty", w.battery]].filter(([, v]) => v);
  const payLines = [["Account Name", co.bank_account_name || ""], ["BSB", co.bank_bsb || ""], ["Account No", co.bank_account_number || ""],
    ["Reference", (co.bank_reference || "").replaceAll("{{invoice_no}}", inv.invoice_no || "")]];
  const wbH = 6.2 + Math.max(wr.length, payLines.length) * 4.5 + 3;
  ensure(wbH + 4);
  const drawKVBox = (title, pairs, x) => {
    doc.setDrawColor(...BORDER); doc.rect(x, y, colW, wbH);
    sectionBar(title, x, colW);
    let yy = y + 10.2;
    pairs.forEach(([k, v]) => {
      setF(7.9, "bold", MUTE); doc.text(k, x + 3, yy);
      setF(8.1, "normal", DARK); doc.text(String(v), x + 42, yy);
      yy += 4.5;
    });
  };
  if (wr.length) drawKVBox("Warranties", wr, mL);
  drawKVBox("Payment — EFT / Bank Transfer", payLines, wr.length ? x2 : mL);
  y += wbH + 4.5;

  // ---------- notes + footer ----------
  if (inv.notes) {
    ensure(10);
    setF(7.8, "italic", MUTE);
    const nl = doc.splitTextToSize(`Notes: ${inv.notes}`, cw);
    doc.text(nl, mL, y); y += nl.length * 3.9 + 3;
  }
  ensure(12);
  doc.setDrawColor(...GREEN); doc.setLineWidth(0.5); doc.line(mL, y, rx, y); doc.setLineWidth(0.2);
  setF(9.2, "bold", GREEN); doc.text("Thank you for choosing Grian Solar Power", W / 2, y + 5.8, { align: "center" });

  return doc;
}

export default function TaxInvoicePanel({ leadId, lead }) {
  const [inv, setInv] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState("");
  const [productNotes, setProductNotes] = useState("");
  const [installDate, setInstallDate] = useState("");
  const [finance, setFinance] = useState("");
  const [financeAmount, setFinanceAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [ov, setOv] = useState({});
  const [products, setProducts] = useState([]);
  const [showAdjust, setShowAdjust] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/leads/${leadId}/tax-invoice`);
    setInv(data.invoice); setCanManage(data.can_manage);
    setPayments((data.invoice?.payments || []).map((p) => ({ ...emptyPay(), ...p })));
    setNotes(data.invoice?.notes || "");
    setProductNotes(data.invoice?.product_notes || "");
    setInstallDate(data.invoice?.installation_date || "");
    setFinance(data.invoice?.finance_provider || "");
    setFinanceAmount(data.invoice?.finance_amount || "");
    setOv(data.invoice?.overrides || {});
    setLoaded(true);
  };
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, [leadId]);

  const save = async (silent) => {
    if (ov.deposit != null && (!Number.isFinite(Number(ov.deposit)) || Number(ov.deposit) < 0)) {
      toast.error("Deposit must be zero or a positive amount"); return null;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/tax-invoice`, {
        notes, product_notes: productNotes, installation_date: installDate || null, finance_provider: finance,
        finance_amount: +financeAmount || 0,
        payments: payments.map((p) => ({ ...p, amount: +p.amount || 0 })),
        overrides: Object.fromEntries(Object.entries(ov).filter(([, v]) => v !== "" && v !== null && v !== undefined)),
      });
      setInv(data); setOv(data.overrides || {}); if (!silent) toast.success("Tax invoice saved");
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm("Regenerate this tax invoice from the customer's signed quote? Manual pricing/product adjustments will be reset (payments and notes are kept).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/tax-invoice`, {
        regenerate: true, notes, product_notes: productNotes, installation_date: installDate || null, finance_provider: finance,
        finance_amount: +financeAmount || 0, payments: payments.map((p) => ({ ...p, amount: +p.amount || 0 })),
      });
      setInv(data); setOv({}); toast.success("Tax invoice regenerated from the signed quote");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setO = (k, v) => setOv((o) => ({ ...o, [k]: v }));

  const uploadReceipt = async (i, file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPayments((ps) => ps.map((p, idx) => idx === i ? { ...p, receipt_id: data.id } : p));
    toast.success("Receipt attached (remember to Save)");
  };

  const downloadPdf = async () => {
    const cur = canManage ? await save(true) : inv; if (!cur) return;
    const doc = await buildInvoicePdf(cur);
    doc.save(`Grian-Tax-Invoice-${cur.invoice_no || "invoice"}.pdf`);
  };

  const sendEmail = async () => {
    const cur = await save(true); if (!cur) return;
    setBusy(true);
    try {
      const doc = await buildInvoicePdf(cur);
      const b64 = doc.output("datauristring");
      await api.post(`/tax-invoices/${cur.id}/send`, { pdf_base64: b64 });
      toast.success("Tax invoice emailed to the customer");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!loaded) return <div className="text-sm text-muted-foreground">Loading…</div>;

  if (!inv) {
    return (
      <div className="bg-card border border-border rounded-md p-6 shadow-sm text-center" data-testid="tax-invoice-empty">
        <Icons.Receipt size={28} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground mb-4">No tax invoice yet. It bills the accepted option (or Recommended / Option 1).</p>
        {canManage
          ? <button onClick={async () => { await save(); }} disabled={busy} data-testid="generate-invoice"
              className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-50">{busy ? "Generating…" : "Generate Tax Invoice"}</button>
          : <p className="text-sm text-muted-foreground">Only super admins &amp; managers can generate invoices.</p>}
      </div>
    );
  }

  const totalReceived = payments.reduce((s, p) => s + (+p.amount || 0), 0);
  const balancePayable = inv.balance_payable ?? inv.total_incl_gst ?? 0;
  const deposit = ov.deposit !== undefined && ov.deposit !== null && ov.deposit !== "" ? Number(ov.deposit) : (inv.quote_deposit ?? inv.deposit ?? 0);
  const financeAmt = +financeAmount || 0;
  const pending = Math.max(balancePayable - deposit - totalReceived - financeAmt, 0);
  const financeLbl = FINANCE_LABELS[finance];

  return (
    <div className="space-y-4" data-testid="tax-invoice-panel">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div><h3 className="font-head font-semibold flex items-center gap-2"><Icons.Receipt size={18} className="text-primary" /> Tax Invoice {inv.invoice_no}</h3>
            <div className="text-xs text-muted-foreground">{inv.customer?.name} · {inv.customer?.email}</div></div>
          <div className="flex flex-wrap gap-2">
            {canManage && <button onClick={regenerate} disabled={busy} data-testid="invoice-regenerate" title="Rebuild from the signed quote" className="px-4 py-2 rounded-md border border-border font-semibold flex items-center gap-1.5 disabled:opacity-50"><Icons.RefreshCw size={15} /> Regenerate</button>}
            <button onClick={downloadPdf} disabled={busy} data-testid="invoice-download" className="px-4 py-2 rounded-md border border-border font-semibold flex items-center gap-1.5 disabled:opacity-50"><Icons.Download size={15} /> PDF</button>
            {canManage && <button onClick={sendEmail} disabled={busy} data-testid="invoice-send" className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold flex items-center gap-1.5 disabled:opacity-50"><Icons.Send size={15} /> Email Customer</button>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="space-y-1 text-sm">
            {inv.system_description && <div className="text-xs font-semibold text-primary mb-1">{inv.system_description}</div>}
            {(inv.line_items || []).map((it, i) => (
              <div key={i} className="flex justify-between gap-2 border-b border-border py-1"><span className="text-muted-foreground">{it.category}</span><span className="font-medium text-right">{itemDesc(it)}</span></div>
            ))}
            {productNotes && <div className="text-xs italic text-muted-foreground pt-1" data-testid="invoice-product-notes-display">{productNotes}</div>}
            <div className="pt-2 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="invoice-install-date-display">
              <Icons.CalendarDays size={13} /> Installation date: <span className="font-semibold text-foreground">{installDate ? fmtAUDate(installDate) : "To be confirmed"}</span>
            </div>
          </div>
          <div className="bg-muted/40 rounded-md p-4 text-sm space-y-1.5" data-testid="invoice-breakdown">
            <div className="flex justify-between font-semibold"><span>Total Cost (Incl GST)</span><span>{currency(inv.total_cost_incl_gst)}</span></div>
            {inv.stc_amount > 0 && <div className="flex justify-between text-muted-foreground"><span>Less {inv.stc_count} STC @ {currency(inv.stc_price)}</span><span>−{currency(inv.stc_amount)}</span></div>}
            {inv.battery_stc_amount > 0 && <div className="flex justify-between text-muted-foreground"><span>Less {inv.battery_stc_count} Battery STC @ {currency(inv.stc_price)}</span><span>−{currency(inv.battery_stc_amount)}</span></div>}
            <div className="flex justify-between font-bold border-t border-border pt-1.5"><span>Final Quote Price (Incl. GST)</span><span>{currency(inv.final_quote_price)}</span></div>
            {inv.vic_rebate > 0 && <div className="flex justify-between text-muted-foreground"><span>Less Solar Victoria Rebate</span><span>−{currency(inv.vic_rebate)}</span></div>}
            {inv.vic_loan > 0 && <div className="flex justify-between text-muted-foreground"><span>Less Solar Victoria Loan</span><span>−{currency(inv.vic_loan)}</span></div>}
            <div className="flex justify-between font-bold text-emerald-700 border-t border-border pt-1.5"><span>Balance Payable</span><span>{currency(balancePayable)}</span></div>
            <div className="flex justify-between gap-2 text-muted-foreground"><span>Deposit</span><span data-testid="invoice-deposit-value">{currency(deposit)}</span></div>
            <div className="flex justify-between text-emerald-600"><span>Balance Payment Received</span><span>{currency(totalReceived)}</span></div>
            {financeLbl && <div className="flex justify-between text-emerald-600" data-testid="invoice-finance-line"><span>Payment via {financeLbl}</span><span>{currency(financeAmt)}</span></div>}
            <div className="flex justify-between gap-2 font-bold border-t border-border pt-1.5"><span>Balance Payment Due</span><span data-testid="invoice-balance-due" className={pending > 0 ? "text-amber-600" : "text-emerald-600"}>{currency(pending)}</span></div>
          </div>
        </div>

        {/* Payments */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-2"><h4 className="font-semibold text-sm">Payments received</h4>
            {canManage && <button onClick={() => setPayments([...payments, emptyPay()])} data-testid="add-payment" className="text-sm px-3 py-1 rounded-md bg-muted font-semibold flex items-center gap-1"><Icons.Plus size={14} /> Add</button>}</div>
          {payments.length === 0 && <p className="text-sm text-muted-foreground">No payments recorded.</p>}
          <div className="space-y-2">
            {payments.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 bg-muted/30 rounded-md p-2" data-testid={`payment-row-${i}`}>
                <input type="date" value={p.date || ""} disabled={!canManage} onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} className="px-2 py-1.5 rounded border border-input bg-background text-sm" />
                <input type="number" placeholder="Amount" value={p.amount} disabled={!canManage} onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} className="w-28 px-2 py-1.5 rounded border border-input bg-background text-sm" data-testid={`payment-amount-${i}`} />
                <select value={p.method} disabled={!canManage} onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, method: e.target.value } : x))} className="px-2 py-1.5 rounded border border-input bg-background text-sm">
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select>
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!p.verified} disabled={!canManage} onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, verified: e.target.checked } : x))} /> Verified</label>
                {p.receipt_id ? <a href={fileUrl(p.receipt_id)} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Receipt</a>
                  : canManage && <label className="text-xs text-primary underline cursor-pointer">Upload receipt<input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadReceipt(i, e.target.files[0])} /></label>}
                {canManage && <button onClick={() => setPayments(payments.filter((_, idx) => idx !== i))} className="text-destructive ml-auto"><Icons.X size={15} /></button>}
              </div>
            ))}
          </div>
        </div>

        {canManage && (
          <div className="border border-dashed border-border rounded-md p-4 mb-4" data-testid="invoice-adjustments">
            <button onClick={() => setShowAdjust((v) => !v)} data-testid="invoice-adjust-toggle" className="w-full flex items-center justify-between text-sm font-semibold">
              <span className="flex items-center gap-2 min-w-0 text-left"><Icons.SlidersHorizontal size={15} className="text-primary shrink-0" /> Invoice adjustments</span>
              <Icons.ChevronDown size={16} className={`transition-transform ${showAdjust ? "rotate-180" : ""}`} />
            </button>
            {showAdjust && (
              <div className="mt-4 space-y-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 [&>div]:min-w-0">
                  <div><label className="label-caps" htmlFor="invoice-deposit-adjustment">Deposit deduction ($)</label>
                    <input id="invoice-deposit-adjustment" type="number" min="0" step="0.01" value={ov.deposit ?? ""} placeholder={String(inv.quote_deposit ?? inv.deposit ?? 0)}
                      onChange={(e) => setO("deposit", e.target.value === "" ? null : +e.target.value)} data-testid="invoice-ov-deposit"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                    <p className="text-[11px] text-muted-foreground mt-1">Invoice only. Recorded payments are unchanged.</p></div>
                  <div><label className="label-caps">Out of pocket ($)</label>
                    <input type="number" step="0.01" value={ov.out_of_pocket ?? ""} placeholder={String(balancePayable)} onChange={(e) => setO("out_of_pocket", e.target.value === "" ? null : +e.target.value)} data-testid="invoice-ov-oop"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                    <div className="text-[11px] text-muted-foreground mt-0.5">Total system price recalculates; STCs stay the same</div></div>
                  <div><label className="label-caps">No. of Solar STCs</label>
                    <input type="number" value={ov.stc_count ?? ""} placeholder={String(inv.stc_count ?? 0)} onChange={(e) => setO("stc_count", e.target.value === "" ? null : +e.target.value)} data-testid="invoice-ov-stc"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                    <div className="text-[11px] text-muted-foreground mt-0.5">@ {currency(inv.stc_price)} each (set in Settings)</div></div>
                  <div><label className="label-caps">No. of Battery STCs</label>
                    <input type="number" value={ov.battery_stc_count ?? ""} placeholder={String(inv.battery_stc_count ?? 0)} onChange={(e) => setO("battery_stc_count", e.target.value === "" ? null : +e.target.value)} data-testid="invoice-ov-bstc"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                </div>
                <div className="flex flex-wrap gap-5 text-sm">
                  {inv.system?.type !== "battery" && <><label className="flex items-center gap-2"><input type="checkbox" checked={ov.include_vic_rebate ?? inv.include_vic_rebate ?? inv.vic_rebate > 0} onChange={(e) => setO("include_vic_rebate", e.target.checked)} data-testid="invoice-ov-vic-rebate" /> Include Solar Victoria Rebate</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={ov.include_vic_loan ?? inv.include_vic_loan ?? inv.vic_loan > 0} onChange={(e) => setO("include_vic_loan", e.target.checked)} data-testid="invoice-ov-vic-loan" /> Include Solar Victoria Loan</label></>}
                </div>
                <div className="text-sm border-t border-border pt-3">
                  <div className="label-caps mb-1.5">Bill-to contact on invoice</div>
                  {(lead?.alt_contact_name || lead?.alt_contact_phone) ? (
                    <label className="flex items-start gap-2" data-testid="invoice-ov-alt-contact-wrap">
                      <input type="checkbox" className="mt-0.5" checked={!!ov.use_alt_contact} onChange={(e) => setO("use_alt_contact", e.target.checked)} data-testid="invoice-ov-alt-contact" />
                      <span>Use alternate contact{lead?.alt_contact_name ? ` — ${lead.alt_contact_name}` : ""}{lead?.alt_contact_phone ? ` (${lead.alt_contact_phone})` : ""}<br />
                        <span className="text-xs text-muted-foreground">Unticked = primary contact ({`${lead?.first_name || ""} ${lead?.last_name || ""}`.trim() || "—"}{lead?.phone ? ` · ${lead.phone}` : ""})</span></span>
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="invoice-ov-alt-hint">Add an Alternate Name / Phone on the Overview tab to bill a different contact here.</p>
                  )}
                </div>
                <div className="grid sm:grid-cols-3 gap-3 [&>div]:min-w-0">
                  {[["panel", "Panel"], ["inverter", "Inverter"], ["battery", "Battery"]].map(([k, lbl]) => (
                    <div key={k}><label className="label-caps">{lbl} model</label>
                      <select value={ov[`${k}_product_id`] || ""} onChange={(e) => setO(`${k}_product_id`, e.target.value || null)} data-testid={`invoice-ov-${k}`}
                        className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
                        <option value="">{inv.products?.[k] ? `From signed quote: ${inv.products[k].brand} ${inv.products[k].model_no}` : "— none —"}</option>
                        {products.filter((p) => p.category === k).map((p) => <option key={p.id} value={p.id}>{p.brand} {p.model_no}</option>)}
                      </select>
                      <input type="number" min="0" value={ov[`${k}_qty`] ?? ""} placeholder={`Qty ${inv.products?.[k]?.qty ?? ""}`} onChange={(e) => setO(`${k}_qty`, e.target.value === "" ? null : +e.target.value)} data-testid={`invoice-ov-${k}-qty`}
                        className="w-full mt-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm" /></div>
                  ))}
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setOv({})} className="px-3 py-1.5 rounded-md border border-border text-sm" data-testid="invoice-ov-clear">Clear adjustments</button>
                  <button onClick={() => save()} disabled={busy} className="px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50" data-testid="invoice-apply">Apply & recalculate</button>
                </div>
              </div>
            )}
          </div>
        )}
        {canManage && (
          <div className="border-t border-border pt-4 mt-4 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-caps">Installation date</label>
                <input type="date" value={installDate} onChange={(e) => setInstallDate(e.target.value)} data-testid="invoice-install-date"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="label-caps">Payment via finance</label>
                <select value={finance} onChange={(e) => setFinance(e.target.value)} data-testid="invoice-finance"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
                  <option value="">None</option>
                  <option value="plenti">Plenti Finance</option>
                  <option value="brighte">Brighte Finance</option>
                </select>
              </div>
            </div>
            {finance && (
              <div>
                <label className="label-caps">Payment received via {FINANCE_LABELS[finance]}</label>
                <input type="number" value={financeAmount} onChange={(e) => setFinanceAmount(e.target.value)} data-testid="invoice-finance-amount"
                  placeholder="0" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
                <div className="text-[11px] text-muted-foreground mt-1">Recorded separately from cash/bank payments. Auto-deducted from Balance Payment Due.</div>
              </div>
            )}
            <div>
              <label className="label-caps">Additional notes (shown below product description)</label>
              <textarea value={productNotes} onChange={(e) => setProductNotes(e.target.value)} rows={2} data-testid="invoice-product-notes"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="label-caps">Invoice notes (shown at bottom of PDF)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="invoice-notes"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
            </div>
            <button onClick={() => save()} disabled={busy} data-testid="invoice-save" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save Invoice"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
